import { ReactNode, useState } from 'react';
import { Button, Empty, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { TrackedCard, TrackedCardFilter, useListTrackedCardsQuery } from '../../../redux/api/kit-staging';
import { StatusLegendPopup } from '../../../components/KitStaging/StatusLegendPopup';
import { MoveDrawer } from '../../../components/KitStaging/MoveDrawer';
import { OliCardTile } from '../../../components/KitStaging/OliCardTile';
import { groupByOli, OliGroup } from './groupByOli';

export type BoardView = 'list' | 'cards';
export type ItemFilter = 'all' | 'kit' | 'material';

interface Props {
	title: string;
	filter: TrackedCardFilter;
	view?: BoardView;
	selectedGroup?: OliGroup | null;
	onSelect?: (g: OliGroup | null) => void;
	/** Restrict the rendered groups to OLIs whose card of this item_type
	 *  is in the board's `current_area`. Default "all". */
	itemFilter?: ItemFilter;
	/** Optional content rendered to the right of the title row (e.g. filter
	 *  chip selector + fullscreen toggle). */
	headerExtra?: ReactNode;
}

/** "waiting_both" -> "Waiting Both", "kit" -> "Kit". */
function humanize(value: string): string {
	return value
		.split('_')
		.map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
		.join(' ');
}

/** Readiness lanes — mirrors the legacy Job Staging Unassigned grouping. */
const STATUS_COLUMNS = [
	{
		key: 'waiting_both',
		label: 'Waiting on Both',
		color: '#8c8c8c',
		statuses: ['waiting_both'],
		tooltip:
			'Neither kit nor material is ready yet. Kitting and material cutting tasks are both pending in ERP.',
	},
	{
		key: 'needs_material',
		label: 'Needs Material',
		color: '#faad14',
		statuses: ['waiting_material'],
		tooltip: "Kit is ready; material cutting hasn't completed yet. Waiting for the saw.",
	},
	{
		key: 'needs_kit',
		label: 'Needs Kit',
		color: '#1890ff',
		statuses: ['waiting_kit'],
		tooltip: "Material is ready or in progress; kit hasn't completed yet. Waiting for kitting.",
	},
	{
		key: 'ready',
		label: 'Ready',
		color: '#52c41a',
		statuses: ['ready', 'ready_priority', 'setup_pending'],
		tooltip:
			'Kit + material both placed on the JSA shelf. Includes Ready (Priority) and Setup Pending (operator has taken it for machine setup).',
	},
	{
		key: 'attention',
		label: 'Needs Attention',
		color: '#ff4d4f',
		statuses: [
			'on_hold',
			'issue',
			'site_mismatch',
			'task_status_mismatch',
			'cancelled',
			'pending_placement',
			'pending_removal',
		],
		tooltip:
			'Exception statuses needing operator action: On Hold (ERP paused), Issue (open ticket), Cancelled, Site Mismatch (wrong machine-group area), Task Status Mismatch (ERP out of sync), Pending Placement / Pending Removal (mid-DUAL move — awaiting the second confirm).',
	},
] as const;

function columnKeyFor(status: string): string {
	const col = STATUS_COLUMNS.find((c) => (c.statuses as readonly string[]).includes(status));
	return col ? col.key : 'attention';
}

function groupStatus(g: OliGroup, area: string): string {
	if (g.kitCard?.current_area === area) return g.kitCard.status;
	if (g.materialCard?.current_area === area) return g.materialCard.status;
	return (g.kitCard ?? g.materialCard)?.status ?? 'waiting_both';
}

export function BoardBase({
	title,
	filter,
	view = 'cards',
	selectedGroup: selectedGroupProp,
	onSelect,
	itemFilter = 'all',
	headerExtra,
}: Props) {
	const { data, isLoading } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const [legendOpen, setLegendOpen] = useState(false);
	const [moveTarget, setMoveTarget] = useState<TrackedCard | null>(null);

	const selected = selectedGroupProp ?? null;

	const allCards = data ?? [];
	const area = filter.current_area as string;

	const groups = groupByOli(allCards).filter((g) => {
		// Default: any side in this area qualifies.
		if (itemFilter === 'all') {
			return g.kitCard?.current_area === area || g.materialCard?.current_area === area;
		}
		// Kit-focused: the kit card must be here. (Material info still shows on
		// the tile via the MAT chip, but the OLI only renders when the kit
		// itself is here.)
		if (itemFilter === 'kit') return g.kitCard?.current_area === area;
		// Material-focused.
		return g.materialCard?.current_area === area;
	});

	const handleTileSelect = (g: OliGroup) => {
		onSelect?.(selected?.oli === g.oli ? null : g);
	};

	const listTable = (
		<Table<OliGroup>
			rowKey="oli"
			loading={isLoading}
			dataSource={groups}
			pagination={false}
			onRow={(record) => ({ onClick: () => handleTileSelect(record) })}
			columns={[
				{
					title: 'OLI',
					key: 'oli',
					render: (_v: unknown, g: OliGroup) => g.job?.job_number || `OLI #${g.oli}`,
				},
				{
					title: 'Part',
					key: 'part',
					render: (_v: unknown, g: OliGroup) => g.job?.part_name || '—',
				},
				{
					title: 'Kit',
					key: 'kit',
					render: (_v: unknown, g: OliGroup) =>
						g.kitCard == null ? '—' : g.kitCard.current_area === area ? '✓' : g.kitCard.current_area,
				},
				{
					title: 'Material',
					key: 'material',
					render: (_v: unknown, g: OliGroup) =>
						g.materialCard == null
							? '—'
							: g.materialCard.current_area === area
								? g.job?.material_partial
									? '~'
									: '✓'
								: g.materialCard.current_area,
				},
				{
					title: 'Status',
					key: 'status',
					render: (_v: unknown, g: OliGroup) => humanize(groupStatus(g, area)),
				},
			]}
		/>
	);

	const columnsBoard = isLoading ? (
		<div style={{ padding: 48, textAlign: 'center' }}>
			<Spin />
		</div>
	) : groups.length === 0 ? (
		<Empty style={{ marginTop: 48 }} description="No cards in this area" />
	) : (
		<div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
			{STATUS_COLUMNS.map((col) => {
				const colGroups = groups.filter((g) => columnKeyFor(groupStatus(g, area)) === col.key);
				return (
					<div key={col.key} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
						<div
							style={{
								textAlign: 'center',
								fontWeight: 700,
								fontSize: 12,
								padding: '6px 8px',
								color: col.color,
								background: '#fff',
								border: `1px solid ${col.color}33`,
								borderBottom: 'none',
								borderRadius: '6px 6px 0 0',
							}}
						>
							<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
								{col.label} <span style={{ fontWeight: 400, opacity: 0.7 }}>({colGroups.length})</span>
								<Tooltip title={col.tooltip} placement="bottom">
									<QuestionCircleOutlined
										style={{
											fontSize: 12,
											opacity: 0.55,
											cursor: 'help',
											marginLeft: 2,
										}}
									/>
								</Tooltip>
							</span>
						</div>
						<div
							style={{
								background: '#f5f5f5',
								padding: 6,
								borderRadius: '0 0 6px 6px',
								display: 'flex',
								flexDirection: 'column',
								gap: 8,
								maxHeight: 'calc(100vh - 240px)',
								overflowY: 'auto',
							}}
						>
							{colGroups.length === 0 ? (
								<div
									style={{
										textAlign: 'center',
										color: '#bfbfbf',
										fontSize: 12,
										padding: '24px 0',
									}}
								>
									No cards
								</div>
							) : (
								colGroups.map((g) => (
									<div key={g.oli} style={{ flexShrink: 0 }}>
										<OliCardTile
											group={g}
											currentArea={area}
											selected={selected?.oli === g.oli}
											onSelect={handleTileSelect}
										/>
									</div>
								))
							)}
						</div>
					</div>
				);
			})}
		</div>
	);

	return (
		<div style={{ padding: 16, width: '100%' }}>
			<Space style={{ justifyContent: 'space-between', width: '100%' }}>
				<Typography.Title level={3} style={{ margin: 0 }}>
					{title}{' '}
					<Typography.Text type="secondary" style={{ fontSize: 14 }}>
						({groups.length})
					</Typography.Text>
				</Typography.Title>
				<Space>
					{headerExtra}
					<Button onClick={() => setLegendOpen(true)} aria-label="Show status legend">
						?
					</Button>
				</Space>
			</Space>

			<div style={{ marginTop: 12 }}>{view === 'list' ? listTable : columnsBoard}</div>

			<StatusLegendPopup open={legendOpen} onClose={() => setLegendOpen(false)} />
			{moveTarget && <MoveDrawer open={!!moveTarget} card={moveTarget} onClose={() => setMoveTarget(null)} />}
		</div>
	);
}
